import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import Styles from './BrandsHub.module.scss';
import BrandCard from 'Components/BrandCard/BrandCard';
import BrandPreview from 'Components/BrandPreview/BrandPreview';
import onBoardingService from 'services/onBoardingService';
import { actions } from 'redux/slices/auth';
import { FiPlus, FiSearch, FiAlertTriangle } from 'react-icons/fi';
import LoadingState from 'Components/LoadingState/LoadingState';
import { success as successToast, error as errorToast } from 'helpers/toaster';
import BrandShareModal from 'Components/Modals/BrandShareModal/BrandShareModal';

const BrandsHub = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);

  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [deleteModalBrand, setDeleteModalBrand] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [shareBrand, setShareBrand] = useState(null);

  useEffect(() => {
    fetchBrands();
  }, []);

  const fetchBrands = async () => {
    try {
      setLoading(true);
      const response = await onBoardingService.getUserBrandInfo();

      // Transform brands data for display
      const brandsArray = response?.data?.brands_data?.map(brandObj => {
        const brandKey = Object.keys(brandObj)[0];
        const brandData = brandObj[brandKey];
        return {
          id: brandData?.id || brandKey,
          business_id: brandData?.business_id,
          brand_name: brandData?.brand_name || 'Unnamed Brand',
          industries: brandData?.industries || [],
          target_audience: brandData?.target_audience || [],
          content_samples_count: brandData?.content_samples_count || 0,
          social_channels_count: brandData?.social_channels_count || 0,
          content_topics: brandData?.content_topics || [],
          description: brandData?.description || '',
          url: brandData?.url || '',
          type: brandData?.type || 'business',
          status: brandData?.status,
          updated_at: brandData?.updated_at
        };
      }) || [];

      setBrands(brandsArray);
      dispatch(actions.updateUser({ user: { user_brands: response?.data } }));
    } catch (error) {
      console.error('Error fetching brands:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBrand = () => {
    navigate('/brand-intelligence');
  };

  const handlePreviewBrand = (brand) => {
    setSelectedBrand(brand);
  };

  const handleClosePreview = () => {
    setSelectedBrand(null);
  };

  const handleEditBrand = (brand) => {
    navigate(`/brand-intelligence/${brand.id}`);
  };

  const handleViewInsights = (brand) => {
    navigate(`/brand-insights/${brand.id}`);
  };

  const handleDeleteClick = (brand) => {
    setDeleteModalBrand(brand);
  };

  const handleShareClick = (brand) => {
    setShareBrand(brand);
  };

  const handleCancelDelete = () => {
    setDeleteModalBrand(null);
  };

  const handleConfirmDelete = async () => {
    if (!deleteModalBrand) return;

    setIsDeleting(true);
    try {
      await onBoardingService.deleteBrand(deleteModalBrand.business_id);
      successToast(`"${deleteModalBrand.brand_name}" has been deleted`);

      // Remove from local state
      setBrands(prev => prev.filter(b => b.id !== deleteModalBrand.id));

      // Update Redux state
      const response = await onBoardingService.getUserBrandInfo();
      dispatch(actions.updateUser({ user: { user_brands: response?.data } }));

      setDeleteModalBrand(null);
    } catch (error) {
      console.error('Error deleting brand:', error);
      errorToast(error.response?.data?.error || 'Failed to delete brand');
    } finally {
      setIsDeleting(false);
    }
  };

  const filteredBrands = brands.filter(brand =>
    brand.brand_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className={Styles.loadingContainer}>
        <LoadingState text="Loading your brands..." />
      </div>
    );
  }

  return (
    <div className={Styles.container}>
      {/* Header */}
      <div className={Styles.header}>
        <div className={Styles.headerContent}>
          <div className={Styles.headerText}>
            <h1 className={Styles.title}>Brand Intelligence Hub</h1>
            <p className={Styles.subtitle}>
              Manage your brand voices, train AI on your content, and maintain consistency across all channels
            </p>
          </div>
          <button className={Styles.createButton} onClick={handleCreateBrand}>
            <FiPlus size={20} />
            Create New Brand
          </button>
        </div>

        {/* Search Bar */}
        {brands.length > 0 && (
          <div className={Styles.searchContainer}>
            <FiSearch className={Styles.searchIcon} />
            <input
              type="text"
              className={Styles.searchInput}
              placeholder="Search brands..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        )}
      </div>

      {/* Stats Overview */}
      <div className={Styles.statsGrid}>
        <div className={`${Styles.statCard} ${Styles.statCardTeal}`}>
          <div className={Styles.statValue}>{brands.length}</div>
          <div className={Styles.statLabel}>Total Brands</div>
        </div>
        <div className={`${Styles.statCard} ${Styles.statCardCoral}`}>
          <div className={Styles.statValue}>
            {brands.reduce((sum, b) => sum + (b.content_samples_count || 0), 0)}
          </div>
          <div className={Styles.statLabel}>Content Samples</div>
        </div>
        <div className={`${Styles.statCard} ${Styles.statCardLavender}`}>
          <div className={Styles.statValue}>
            {brands.reduce((sum, b) => sum + (b.social_channels_count || 0), 0)}
          </div>
          <div className={Styles.statLabel}>Connected Channels</div>
        </div>
      </div>

      {/* Brands Grid */}
      {filteredBrands.length > 0 ? (
        <div className={Styles.brandsGrid}>
          {filteredBrands.map((brand) => (
            <BrandCard
              key={brand.id}
              brand={brand}
              onClick={() => handlePreviewBrand(brand)}
              onEdit={handleEditBrand}
              onDelete={handleDeleteClick}
              onViewInsights={handleViewInsights}
              onShare={handleShareClick}
            />
          ))}
        </div>
      ) : brands.length === 0 ? (
        <div className={Styles.emptyState}>
          <div className={Styles.emptyStateIcon}>🎨</div>
          <h3 className={Styles.emptyStateTitle}>No brands yet</h3>
          <p className={Styles.emptyStateText}>
            Create your first brand to start training your AI on your unique voice and style
          </p>
          <button className={Styles.emptyStateButton} onClick={handleCreateBrand}>
            <FiPlus size={20} />
            Create Your First Brand
          </button>
        </div>
      ) : (
        <div className={Styles.emptyState}>
          <div className={Styles.emptyStateIcon}>🔍</div>
          <h3 className={Styles.emptyStateTitle}>No brands found</h3>
          <p className={Styles.emptyStateText}>
            Try adjusting your search query
          </p>
        </div>
      )}

      {/* Brand Preview Panel */}
      {selectedBrand && (
        <BrandPreview
          brand={selectedBrand}
          onClose={handleClosePreview}
          onEdit={handleEditBrand}
          onShare={handleShareClick}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteModalBrand && (
        <div className={Styles.modalOverlay}>
          <div className={Styles.deleteModal}>
            <div className={Styles.deleteModalIcon}>
              <FiAlertTriangle size={24} />
            </div>
            <h3 className={Styles.deleteModalTitle}>Delete Brand</h3>
            <p className={Styles.deleteModalText}>
              Are you sure you want to delete <strong>{deleteModalBrand.brand_name}</strong>?
              This action cannot be undone and will remove all associated content samples
              and training data.
            </p>
            <div className={Styles.deleteModalActions}>
              <button
                className={Styles.cancelButton}
                onClick={handleCancelDelete}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                className={Styles.deleteButton}
                onClick={handleConfirmDelete}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete Brand'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Share Brand Modal */}
      <BrandShareModal
        show={!!shareBrand}
        onHide={() => setShareBrand(null)}
        brand={shareBrand}
      />
    </div>
  );
};

export default BrandsHub;
